import { useEffect, useState, useCallback, Fragment } from "react";
import api, { apiError } from "@/lib/api";
import { Page, PageHeader, Empty } from "@/components/shared";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ClipboardList, ChevronRight, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import {
  OppdragCustomerSection, MONTHS_NB, oppdragYearOptions,
  defaultOppdragYear, defaultOppdragMonth,
} from "@/components/OppdragExecution";

function Stat({ label, value, hint }) {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <p className="label-caps">{label}</p>
      <p className="font-display text-2xl mt-2">{value}</p>
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </div>
  );
}

export default function Oppdragsutforelse() {
  const [year, setYear] = useState(defaultOppdragYear());
  const [month, setMonth] = useState(defaultOppdragMonth());
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(() => {
    api.get(`/oppdragsutforelse/overview?year=${year}&month=${month}`)
      .then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  const routines = data?.routines || [];

  return (
    <Page>
      <PageHeader title="Oppdragsutførelse" subtitle="Følg opp utførelse og kvalitetssikring av oppdragsrutiner per kunde og periode" testid="oppdrag-header">
        <div className="flex items-center gap-2">
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="w-36 bg-secondary border-border" data-testid="oppdrag-overview-month"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover border-border max-h-72">
              {MONTHS_NB.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-28 bg-secondary border-border" data-testid="oppdrag-overview-year"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-popover border-border">
              {oppdragYearOptions().map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </PageHeader>

      {!data ? (
        <p className="text-muted-foreground">Laster…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="oppdrag-totals">
            <Stat label="Kunder med oppdrag" value={data.totals.customers} />
            <Stat label="Aktive oppdragsrutiner" value={data.totals.routines} />
            <Stat label="Utførte punkter" value={`${data.totals.done_points} / ${data.totals.total_points}`} hint={`${MONTHS_NB[month - 1]} ${year}`} />
            <Stat label="Kvalitetssikrede punkter" value={`${data.totals.qa_points} / ${data.totals.total_points}`} />
          </div>

          <div className="bg-card border border-border rounded-xl p-5 mt-4">
            <p className="label-caps mb-3">Fremdrift per rutine (kunder komplett)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {routines.map((r) => {
                const t = data.totals.by_routine[r.id] || { applicable: 0, done_complete: 0, qa_complete: 0 };
                const donePct = t.applicable ? Math.round((t.done_complete / t.applicable) * 100) : 0;
                const qaPct = t.applicable ? Math.round((t.qa_complete / t.applicable) * 100) : 0;
                return (
                  <div key={r.id} data-testid={`oppdrag-progress-${r.id}`}>
                    <p className="text-xs font-medium truncate" title={r.title}>{r.title}</p>
                    <p className="text-[11px] text-muted-foreground mb-1">{t.applicable} kunder</p>
                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                      <span className="w-8">Utf.</span>
                      <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
                        <div className="h-full bg-sky-500 transition-all" style={{ width: `${donePct}%` }} />
                      </div>
                      <span className="w-9 text-right">{t.done_complete}/{t.applicable}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-1">
                      <span className="w-8">KS</span>
                      <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${qaPct}%` }} />
                      </div>
                      <span className="w-9 text-right">{t.qa_complete}/{t.applicable}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {data.customers.length === 0 ? (
            <div className="mt-6">
              <Empty icon={ClipboardList} title="Ingen kunder med oppdragsrutiner" hint="Kunder vises her når de har relevant produkt eller er omfattet av en foretaksomfattende oppdragsrutine." />
            </div>
          ) : (
            <div className="mt-6 overflow-x-auto bg-card border border-border rounded-xl">
              <table className="w-full text-sm min-w-[900px]">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="px-4 py-3 label-caps sticky left-0 bg-card z-10">Kunde</th>
                    {routines.map((r) => (
                      <th key={r.id} className="px-3 py-3 label-caps text-center whitespace-nowrap" title={r.title}>{r.title}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.customers.map((c) => {
                    const isOpen = expanded === c.customer_id;
                    return (
                      <Fragment key={c.customer_id}>
                        <tr onClick={() => setExpanded(isOpen ? null : c.customer_id)}
                          className="border-b border-border/50 hover:bg-secondary/30 cursor-pointer" data-testid={`oppdrag-row-${c.customer_id}`}>
                          <td className="px-4 py-3 sticky left-0 bg-card">
                            <div className="flex items-center gap-2">
                              {isOpen ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
                              <div>
                                <p className="font-medium">{c.customer_name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {c.org_number || "—"}{c.engagement_manager_name ? ` · ${c.engagement_manager_name}` : ""}
                                </p>
                              </div>
                            </div>
                          </td>
                          {routines.map((r) => {
                            const e = c.executions[r.id];
                            if (!e) {
                              return <td key={r.id} className="px-3 py-3 text-center text-muted-foreground/40" data-testid={`oppdrag-cell-${c.customer_id}-${r.id}`}>–</td>;
                            }
                            return (
                              <td key={r.id} className="px-3 py-3 text-center" data-testid={`oppdrag-cell-${c.customer_id}-${r.id}`}>
                                <div className="flex flex-col items-center gap-0.5 text-xs">
                                  <span className={e.done_complete ? "text-sky-400 font-medium" : "text-muted-foreground"}>U {e.done_count}/{e.total}</span>
                                  <span className={e.qa_complete ? "text-emerald-400 font-medium" : "text-muted-foreground"}>KS {e.qa_count}/{e.total}</span>
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                        {isOpen && (
                          <tr className="border-b border-border/50 bg-secondary/10">
                            <td colSpan={routines.length + 1} className="px-4 py-4" data-testid={`oppdrag-expand-${c.customer_id}`}>
                              <OppdragCustomerSection customerId={c.customer_id} year={year} month={month} hideSelector onChange={load} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Page>
  );
}
